import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";

import Navbar from "../../components/Navbar";
import PageContent from "../../components/PageContent";
import Header from "../../components/Header";

import { useNavigate, useLocation } from "react-router-dom";

import "./styles.css";

import { ReactComponent as OnlineWorld } from "../../assets/online-world.svg";

import StatusChip from "../../components/SmallComponents/StatusChip";
import MainButton from "../../components/SmallComponents/MainButton";

import {
  Snackbar,
  Divider,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { DataGrid } from "@mui/x-data-grid";
import SendTimeExtensionIcon from "@mui/icons-material/SendTimeExtension";
import ShareIcon from "@mui/icons-material/Share";
import EditIcon from "@mui/icons-material/Edit";
import FemaleIcon from "@mui/icons-material/Female";
import MaleIcon from "@mui/icons-material/Male";

import UsersSelectionModal from "../../components/UsersSelectionModal";

import api from "../../helpers/api.js";

const labels = {
  Genders: {
    MALE: "Masculino",
    FEMALE: "Feminino",
    GENERIC: "Genérico",
  },

  Goals: {
    AESTHETICS: "Estética",
    HEALTH: "Saúde",
    MUSCLE_MASS: "Ganho de massa",
    STRENGTH: "Força",
    FAT_LOSS: "Emagrecimento",
  },

  AgeGroups: {
    25: "Até 25 anos",
    40: "26 a 40 anos",
    60: "41 a 60 anos",
    80: "61 a 80 anos",
    GENERIC: "Genérico",
  },
};

const filters = [
  {
    key: "libraryWorkoutGender",
    name: "Gênero",
    options: [
      {
        label: "Masculino",
        value: "MALE",
        icon: <MaleIcon />,
      },
      {
        label: "Feminino",
        value: "FEMALE",
        icon: <FemaleIcon />,
      },
    ],
  },
  {
    key: "libraryWorkoutAmbient",
    name: "Ambiente",
    options: [
      {
        label: "Academia",
        value: "GYM",
      },
      {
        label: "Em casa",
        value: "HOME",
      },
    ],
  },
  {
    key: "libraryWorkoutDifficulty",
    name: "Dificuldade",
    options: [
      {
        label: 1,
        value: "BEGINNER",
      },
      {
        label: 2,
        value: "INTERMEDIATE",
      },
      {
        label: 3,
        value: "ADVANCED",
      },
    ],
  },
  {
    key: "libraryWorkoutAgeGroup",
    name: "Faixa etária",
    options: [
      {
        label: "Até 25 anos",
        value: "25",
      },
      {
        label: "De 26 a 40 anos",
        value: "40",
      },
      {
        label: "De 41 a 60 anos",
        value: "60",
      },
      {
        label: "60 anos ou mais",
        value: "80",
      },
    ],
  },
  {
    key: "libraryWorkoutGoal",
    name: "Objetivo principal",
    options: [
      {
        label: "Estética",
        value: "AESTHETICS",
      },
      {
        label: "Saúde",
        value: "HEALTH",
      },
      {
        label: "Ganho de massa",
        value: "MUSCLE_MASS",
      },
      {
        label: "Força",
        value: "STRENGTH",
      },
      {
        label: "Emagrecimento",
        value: "FAT_LOSS",
      },
    ],
  },
];

export default function WorkoutsLibrary() {
  document.title = "Biblioteca de treinos | Ginastee";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState(null);

  const [sendWorkoutModal, setSendWorkoutModal] = useState({});

  const [filtersState, setFiltersState] = useState(getFilters());

  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 10);
  }, [pathname]);

  useEffect(() => {
    if (loading) {
      handleLoadWorkouts();
    }
  }, [loading]);

  useEffect(() => {
    if (snackbarMessage) {
      setTimeout(() => {
        setSnackbarMessage(null);
      }, 3000);
    }
  }, [snackbarMessage]);

  function getFilters() {
    const result = {};
    filters.forEach((filter) => (result[filter.key] = false));
    return result;
  }

  async function handleLoadWorkouts() {
    try {
      let workouts = await api.get("/gym/workouts/library");

      workouts = workouts.data;

      if (workouts) {
        workouts = Object.keys(workouts).map((workoutId) => {
          const info = workouts[workoutId];
          info.workoutId = workoutId;
          info.daysTotal = info.workoutDays.length;
          return info;
        });
        workouts = workouts.map((data) => {
          return {
            ...data,
            GenderColor: (() => {
              return {
                MALE: "blue",
                FEMALE: "pink",
                GENERIC: "green",
              }[data.libraryWorkoutGender];
            })(),
            GenderLabel: labels.Genders[data.libraryWorkoutGender],
            GoalLabel: labels.Goals[data.libraryWorkoutGoal],
            AgeGroupLabel: labels.AgeGroups[data.libraryWorkoutAgeGroup],
          };
        });

        setRows([...workouts]);
      }

      setLoading(false);
    } catch (e) {
      setSnackbarMessage(
        "Ocorreu um arro ao carrregar os usuários, por favor tente mais tarde"
      );
      setLoading(false);
    }
  }

  const columns = [
    {
      field: "name",
      headerName: "Nome da ficha",
      sortable: false,
      flex: 1,
      minWidth: 100,
    },
    {
      field: "daysTotal",
      headerName: "Dias",
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "GenderLabel",
      headerName: "Gênero",
      sortable: false,
      headerAlign: "center",
      renderCell: (params) => (
        <StatusChip
          label={params.row.GenderLabel}
          color={params.row.GenderColor}
        />
      ),
    },
    {
      field: "GoalLabel",
      headerName: "Objetivo",
      sortable: false,
      align: "center",
      headerAlign: "center",
      minWidth: 150,
    },
    {
      field: "AgeGroupLabel",
      headerName: "Faixa etária",
      minWidth: 150,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "actions",
      headerName: "",
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <div>
          <Tooltip title="Enviar variação" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSendVariation(params.row);
              }}
              children={<SendTimeExtensionIcon color="customBlue" />}
            />
          </Tooltip>

          <Tooltip title="Encaminhar" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleShareWorkout(params.row);
              }}
              children={<ShareIcon color="customBlue" />}
            />
          </Tooltip>

          <Tooltip title="Editar" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdiWorkout(params.row);
              }}
              children={<EditIcon color="customBlue" />}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  function getFilteredRows() {
    let data = [...rows];

    data = data.filter((workout) => {
      let valid = true;
      Object.keys(filters).forEach((filter) => {
        const key = filters[filter].key;
        if (filtersState[key]) {
          console.log(workout[key], filtersState[key]);
          if (workout[key] !== filtersState[key]) {
            valid = false;
          }
        }
      });
      return valid;
    });

    return data;
  }

  function handleRedirectToWorkoutCreation() {
    console.log("Go to workout creation");
    navigate("/newworkout", {
      state: {
        createNewModel: true,
        mode: "LIBRARY_WORKOUT",
      },
    });
  }
  function handleSendVariation(workout) {
    console.log("Send variation", workout);
    const data = {
      info: workout,
      mode: "SEND_VARIATION",
      open: true,
    };
    setSendWorkoutModal({ ...data });
  }
  function handleEdiWorkout(workout) {
    console.log("Edit", workout);
  }
  function handleShareWorkout(workout) {
    console.log("share", workout);
    const data = {
      info: workout,
      mode: "SHARE",
      open: true,
    };
    setSendWorkoutModal({ ...data });
  }

  async function handleSendWorkoutToUsers(users){
    const ids = users.map(user => user.userID)

    try{
      const result = await api.post('/gym/workouts/library/send', {
        userIds: ids,
        workoutId: sendWorkoutModal.info.workoutId
      })
      setSnackbarMessage('Treinos enviados com sucesso')
      closeUsersModal()
    } catch(e){

    }
  }

  function openUsersModal(){
    setSendWorkoutModal({ ...sendWorkoutModal, open: true })
  }
  function closeUsersModal(){
    setSendWorkoutModal({ ...sendWorkoutModal, open: false })
  }

  return (
    <>
      <UsersSelectionModal
        mainButtonText={"Enviar treino"}
        open={sendWorkoutModal.open}
        title={"Enviar treino " + sendWorkoutModal?.info?.name}
        allowMultipleSelection={sendWorkoutModal.mode === "SHARE"}
        onClose={closeUsersModal}
        onSubmit={(usersSelected) => {
          if (sendWorkoutModal.mode === "SHARE") {
            console.log(usersSelected)
            handleSendWorkoutToUsers(usersSelected)
          } else {
            navigate("/newworkout", {
              state: {
                ...usersSelected[0],
                userId: usersSelected[0].id,
                preLoadedWorkout: sendWorkoutModal?.info,
              },
            });
          }
        }}
      />
      <Navbar />
      <PageContent page="wl">
        <Snackbar
          open={!!snackbarMessage}
          autoHideDuration={6000}
          onClose={() => setSnackbarMessage(null)}
          message={snackbarMessage}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        />
        <Header />
        <div className="mt-5">
          <Typography variant="h2" component="h2" className="left-border mt-4">
            Biblioteca de treinos
          </Typography>
          <Typography variant="h4" component="h4" className="mt-2 fade mb-2">
            Crie sua própria biblioteca de treinos para enviar aos alunos
          </Typography>
          <MainButton
            onClick={handleRedirectToWorkoutCreation}
            startIcon={<SendIcon />}
            label="Criar novo treino"
          />
        </div>

        <div className="mt-4 pb-6">
          {loading ? (
            <CircularProgress
              thickness={5}
              color="secondary"
              className="loading-users"
            />
          ) : rows.length === 0 ? (
            <NoWorkoutsFound
              setSnackbarMessage={setSnackbarMessage}
              onCTAClick={handleRedirectToWorkoutCreation}
            />
          ) : (
            <>
              <div className="filters-block px-3 py-1">
                <Typography variant="h4" component="h4" className="mt-2 fade">
                  Filtrar por
                </Typography>
                <Divider className="mb-2 mt-1" />
                <div id="list-filters">
                  {Object.keys(filters).map((filter) => (
                    <div className="list-filter-group-area">
                      <Typography variant="span">
                        {filters[filter].name}
                      </Typography>
                      <div className="list-filter-group mt-1">
                        {filters[filter].options.map((option, i) => {
                          const key = filters[filter].key;
                          const active = filtersState[key] === option.value;
                          const inactive =
                            filtersState[key] &&
                            filtersState[key] !== option.value;
                          return (
                            <>
                              <Button
                                variant={"text"}
                                size="small"
                                className={
                                  "btn " + (active ? "active-btn" : "")
                                }
                                startIcon={option.icon}
                                style={{
                                  opacity: inactive ? 0.5 : 1,
                                }}
                                onClick={() => {
                                  setFiltersState({
                                    ...filtersState,
                                    [key]: active ? undefined : option.value,
                                  });
                                }}
                              >
                                {option.label}
                              </Button>
                              {i < filters[filter].options.length - 1 && (
                                <Divider
                                  style={{
                                    marginTop: "6px",
                                    marginBottom: "6px",
                                  }}
                                  orientation="vertical"
                                  flexItem
                                />
                              )}
                            </>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <DataGrid
                disableSelectionOnClick
                className="mt-4"
                color="secondary"
                disableColumnMenu={true}
                hideFooter={true}
                autoHeight
                rows={getFilteredRows()}
                columns={columns}
                getRowId={(row) => row?.workoutId}
                components={{
                  NoRowsOverlay: () => (
                    <Typography style={{
                      textAlign: 'center'
                    }} height="100%" alignItems="center" className="px-2 py-4" justifyContent="center">
                      Nenhum treino foi encontrado para estes filtros
                    </Typography>
                  ),
                }}
              
              />
            </>
          )}
        </div>
      </PageContent>
    </>
  );
}

const NoWorkoutsFound = ({ setSnackbarMessage, onCTAClick }) => {
  return (
    <div className="no-users-found">
      <OnlineWorld />
      <div>
        <Typography variant="h3" className="mb-1 mt-7">
          Você ainda não tem nenhum treino em sua biblioteca
        </Typography>
        <Typography variant="p">
          Pressione abaixo para criar o primeiro treino da sua biblioteca
        </Typography>
        <br />
        <MainButton
          className="mt-2"
          small
          color="primary"
          label="criar treino"
          onClick={onCTAClick}
        />
      </div>
    </div>
  );
};
